"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __createBinding = this && this.__createBinding || (Object.create ? function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  Object.defineProperty(o, k2, {
    enumerable: true,
    get: function get() {
      return m[k];
    }
  });
} : function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  o[k2] = m[k];
});

var __setModuleDefault = this && this.__setModuleDefault || (Object.create ? function (o, v) {
  Object.defineProperty(o, "default", {
    enumerable: true,
    value: v
  });
} : function (o, v) {
  o["default"] = v;
});

var __importStar = this && this.__importStar || function (mod) {
  if (mod && mod.__esModule) return mod;
  var result = {};
  if (mod != null) for (var k in mod) {
    if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
  }

  __setModuleDefault(result, mod);

  return result;
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.YouTubePlayerAPIProvider = exports.withYouTubePlayerAPI = exports.ifNotYouTubePlayerAPILoaded = exports.ifYouTubePlayerAPILoaded = exports.useYouTubePlayerAPI = exports.withYouTubePlayerTransformer = exports.withYouTubeFormSrcSnippet = exports.withYouTubeFormHeader = exports.withYouTubePlayerSettings = exports.asBodilessYouTube = exports.asBaseBodilessYouTube = void 0;

var react_1 = __importStar(require("react"));

var lodash_1 = require("lodash");

var core_1 = require("@bodiless/core");

var fclasses_1 = require("@bodiless/fclasses");

var withFormHeader_1 = __importDefault(require("./withFormHeader"));

var withFormSnippet_1 = __importDefault(require("./withFormSnippet"));

var Iframe_1 = require("./Iframe"); // https://stackoverflow.com/a/9102270


var extractVideoIdFromUrl = function extractVideoIdFromUrl(url) {
  // eslint-disable-next-line no-useless-escape
  var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  var match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : undefined;
};

var isValidYouTubeUrl = extractVideoIdFromUrl;

var withYouTubePlayerSettings = function withYouTubePlayerSettings(settings) {
  return fclasses_1.addProps({
    playerSettings: settings
  });
};

exports.withYouTubePlayerSettings = withYouTubePlayerSettings;
/*
* adjust loop settings per https://developers.google.com/youtube/player_parameters#loop
*/

var adjustLoopPlayerSettings = function adjustLoopPlayerSettings(settings, videoId) {
  return __assign(__assign({}, settings), settings.loop && videoId && {
    playlist: videoId,
    version: 3
  });
};

var withYouTubePlayerTransformer = function withYouTubePlayerTransformer(Component) {
  var WithYouTubePlayerTransformer = function WithYouTubePlayerTransformer(props) {
    var playerSettings = props.playerSettings,
        src = props.src,
        rest = __rest(props, ["playerSettings", "src"]);

    var videoId = src ? extractVideoIdFromUrl(src) : '';
    var src$ = "https://www.youtube.com/embed/" + videoId;
    var url = new URL(src$);

    if (playerSettings !== undefined && videoId !== undefined) {
      var playerSettings$ = adjustLoopPlayerSettings(playerSettings, videoId);
      Object.entries(playerSettings$).forEach(function (setting) {
        var key = setting[0],
            value = setting[1];
        url.searchParams.set(key, String(value));
      });
    }

    return react_1.default.createElement(Component, __assign({}, rest, {
      src: url.toString()
    }));
  };

  WithYouTubePlayerTransformer.displayName = 'WithYouTubePlayerTransformer';
  return WithYouTubePlayerTransformer;
};

exports.withYouTubePlayerTransformer = withYouTubePlayerTransformer;
var YouTubePlayerAPIContext = react_1.default.createContext({
  isLoaded: false
});

var YouTubePlayerAPIProvider = function YouTubePlayerAPIProvider(_a) {
  var children = _a.children;

  var _b = react_1.useState(false),
      isLoaded = _b[0],
      setIsLoaded = _b[1];

  react_1.useEffect(function () {
    var scriptId = 'youtubeplayerapiscript';
    var scriptSrc = 'https://www.youtube.com/iframe_api';

    if (!document.getElementById(scriptId)) {
      var script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.async = true;
      script.src = scriptSrc;
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  });
  react_1.useEffect(function () {
    window.onYouTubeIframeAPIReady = function () {
      setIsLoaded(true);
    };
  });
  return react_1.default.createElement(YouTubePlayerAPIContext.Provider, {
    value: {
      isLoaded: isLoaded
    }
  }, children);
};

exports.YouTubePlayerAPIProvider = YouTubePlayerAPIProvider;

var withYouTubePlayerAPI = function withYouTubePlayerAPI(Component) {
  var WithYouTubePlayerAPI = function WithYouTubePlayerAPI(props) {
    return react_1.default.createElement(YouTubePlayerAPIProvider, null, react_1.default.createElement(Component, __assign({}, props)));
  };

  WithYouTubePlayerAPI.displayName = 'WithYouTubePlayerAPI';
  return WithYouTubePlayerAPI;
};

exports.withYouTubePlayerAPI = withYouTubePlayerAPI;

var useYouTubePlayerAPI = function useYouTubePlayerAPI() {
  return react_1.useContext(YouTubePlayerAPIContext);
};

exports.useYouTubePlayerAPI = useYouTubePlayerAPI;

var ifYouTubePlayerAPILoaded = function ifYouTubePlayerAPILoaded() {
  return react_1.useContext(YouTubePlayerAPIContext).isLoaded;
};

exports.ifYouTubePlayerAPILoaded = ifYouTubePlayerAPILoaded;

var ifNotYouTubePlayerAPILoaded = function ifNotYouTubePlayerAPILoaded() {
  return !react_1.useContext(YouTubePlayerAPIContext).isLoaded;
};

exports.ifNotYouTubePlayerAPILoaded = ifNotYouTubePlayerAPILoaded;
var withYouTubeFormSrcSnippet = withFormSnippet_1.default({
  nodeKeys: 'src',
  defaultData: {
    src: ''
  },
  snippetOptions: {
    renderForm: function renderForm(_a) {
      var formState = _a.formState;
      var errors = formState.errors;

      var _b = core_1.useMenuOptionUI(),
          ComponentFormLabel = _b.ComponentFormLabel,
          ComponentFormText = _b.ComponentFormText,
          ComponentFormWarning = _b.ComponentFormWarning;

      var validate = react_1.useCallback(function (value) {
        return !value || !isValidYouTubeUrl(value) ? 'Invalid YouTube URL specified.' : undefined;
      }, []);
      return react_1.default.createElement(react_1.default.Fragment, {
        key: "src"
      }, react_1.default.createElement(ComponentFormLabel, {
        htmlFor: "src"
      }, "URL"), react_1.default.createElement(ComponentFormText, {
        field: "src",
        placeholder: "https://",
        validate: validate,
        validateOnChange: true,
        validateOnBlur: true
      }), errors && errors.src && react_1.default.createElement(ComponentFormWarning, null, errors.src));
    }
  }
});
exports.withYouTubeFormSrcSnippet = withYouTubeFormSrcSnippet;
var withYouTubeFormHeader = withFormHeader_1.default({
  title: 'YouTube Configuration'
});
exports.withYouTubeFormHeader = withYouTubeFormHeader;
var asBaseBodilessYouTube = Iframe_1.asBaseBodilessIframe;
exports.asBaseBodilessYouTube = asBaseBodilessYouTube;

var asBodilessYouTube = function asBodilessYouTube(nodeKeys, defaultData, useOverrides) {
  return lodash_1.flowRight(asBaseBodilessYouTube(nodeKeys, defaultData, useOverrides), withYouTubeFormHeader, withYouTubeFormSrcSnippet, withYouTubePlayerTransformer);
};

exports.asBodilessYouTube = asBodilessYouTube;
var YouTube = asBodilessYouTube()('iframe');
exports.default = YouTube;